import { useCallback } from "react";
import { ServerMessages, useSocketContext } from "../context/SocketContext";
import { CarsPerStationServerResponse, ClientType, ClientTypesMigrationReport, MonthlyRevenueReportEntry, QuarterlyUniqueClient, SMSLogEntry, TopFiftyClient, YearComparisonReport } from "../types/Reports";
import { setStartDayHours, setStartOfNextDay } from "../utils/time";

const useReportsService = () => {
    const { emit } = useSocketContext();

    const fetchClientTypesReportData = useCallback(
        async (
            minDate: number,
            maxDate: number,
        ): Promise<ClientType[]> => {
            const response = await emit({
                message: ServerMessages.getReportCustomerTypes,
                criteria: {
                    minDate,
                    maxDate,
                },
                options: {
                    categories: [
                        { name: "Clienti rari", minEntries: 0, maxEntries: 1 },
                        { name: "Clienti ocazionali", minEntries: 2, maxEntries: 3 },
                        { name: "Clienti fideli", minEntries: 4, maxEntries: 7 },
                        { name: "Clienti inraiti", minEntries: 8, maxEntries: 1000 }
                    ]
                },
            });

            return response?.data ?? [];
        }, [emit],
    );

    const fetchTopFiftyClientsReportData = useCallback(
        async (
            minDate: number,
            maxDate: number,
        ): Promise<TopFiftyClient[]> => {
            const response = await emit({
                message: ServerMessages.getReportTopFiftyClients,
                criteria: {
                    // Send the exclusive upper bound as next local midnight (browser = car wash timezone),
                    // matching the Client-Types report so the two agree. The server must NOT re-snap this.
                    minDate: minDate ? setStartDayHours(minDate).getTime() : null,
                    maxDate: maxDate ? setStartOfNextDay(maxDate).getTime() : null,
                },
            });

            return response?.data ?? [];
        },
        [emit],
    );

    const fetchQuarterlyUniqueClientsReportData = useCallback(
        async (): Promise<QuarterlyUniqueClient[]> => {
            const response = await emit({
                message: ServerMessages.getReportQuarterlyCustomers,
            });

            return response?.data ?? [];
        }, [emit],
    );

    const fetchYearComparisonReportData = useCallback(
        async (referenceYear: number): Promise<YearComparisonReport> => {
            const response = await emit({
                message: ServerMessages.getReportYearComparisons,
                referenceYear,
            });

            return response?.data ?? [];
        }, [emit],
    );

    const fetchClientTypesMigrationReportData = useCallback(
        async (
            minDate1: Date,
            maxDate1: Date,
            minDate2: Date,
            maxDate2: Date,
        ): Promise<ClientTypesMigrationReport> => {
            const response = await emit({
                message: ServerMessages.getReportCustomerTypesComparison,
                criteria: {
                    minDate1,
                    maxDate1,
                    minDate2,
                    maxDate2,
                },
                options: {
                    categories: [
                        { name: "Clienti rari", minEntries: 0, maxEntries: 1 },
                        { name: "Clienti ocazionali", minEntries: 2, maxEntries: 3 },
                        { name: "Clienti fideli", minEntries: 4, maxEntries: 7 },
                        { name: "Clienti inraiti", minEntries: 8, maxEntries: 1000 }
                    ]
                },
            });

            return response?.data ?? [];
        }, [emit],
    );

    const fetchMonthlyRevenueReportData = useCallback(
        async (): Promise<MonthlyRevenueReportEntry> => {
            const response = await emit({
                message: ServerMessages.getReportMonthlyRevenue,
            });

            return response?.data ?? [];
        },
        [emit],
    );

    const updateMonthlyRevenueReport = useCallback(
        async (
            year: number,
            month: number,
            amount: number,
        ): Promise<boolean> => {
            const response = await emit({
                message: ServerMessages.updateMonthlyRevenue,
                options: {
                    year,
                    month,
                    amount,
                }
            });

            return response?.result === 'success';
        }, [emit],
    );

    const fetchSmsReportData = useCallback(
        async (
            minDate: number,
            maxDate: number,
        ): Promise<SMSLogEntry[]> => {
            const response = await emit({
                message: ServerMessages.getReportSMSLog,
                criteria: {
                    minDate: minDate ? new Date(minDate).setHours(0, 0, 0, 0) : null,
                    maxDate: maxDate ? new Date(maxDate).setHours(23, 59, 59, 999) : null,
                },
            });

            return response?.data ?? [];
        }, [emit],
    );

    const fetchCarsPerStationComparisonReportData = useCallback(
        async (): Promise<CarsPerStationServerResponse> => {
            const response = await emit({
                message: ServerMessages.getReportCarsPerStationComparison,
            });

            return response?.data ?? null;
        }, [emit],
    );

    const getOldestEntry = useCallback(
        async (): Promise<string> => {
            const response = await emit({
                message: ServerMessages.getOldestEntry,
            });

            return response?.data ?? [];
        }, [emit],
    );


    return {
        fetchClientTypesReportData,
        fetchTopFiftyClientsReportData,
        fetchQuarterlyUniqueClientsReportData,
        fetchYearComparisonReportData,
        fetchClientTypesMigrationReportData,
        fetchMonthlyRevenueReportData,
        updateMonthlyRevenueReport,
        fetchSmsReportData,
        fetchCarsPerStationComparisonReportData,
        getOldestEntry,
    };
};

export default useReportsService;
